import { useDispatch } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import noop from 'utils/noop';
import { BUTTON_SIZES, PrimaryButton } from 'ui/button';
import { MODAL_SIZES, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { GoalAttributionMode } from 'modules/goals/types';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer/utils';
import { useUpdateGoalVisibilityMutation } from 'modules/missionControl/hooks/useUpdateGoalVisibilityMutation';
import { ToastMessage } from 'modules/goals/lib/components/ToastMessage';
import { useMissionControlContext } from 'modules/missionControl/contexts/MissionControlContext';
import { CreateGoalForm } from '../CreateGoalForm';
import {
  CreateGoalFormValues,
  withCreateGoalFormProvider,
} from '../CreateGoalForm/CreateGoalFormProvider';
import { useCreateGoalMutation } from '../../hooks/useCreateGoalMutation';
import {
  EVENT_TYPE_DELIMITER,
  EVENT_NAME_FIELD,
  GOAL_NAME_FIELD,
} from '../CreateGoalForm/CreateGoalForm.constants';
import { match } from 'ts-pattern';

export const CREATE_GOAL_MODAL_TEST_ID = 'create-goal-modal';
export const CREATE_GOAL_BUTTON_TEST_ID = 'create-goal-button';

type CreateGoalModalProps = {
  onClose?: () => void;
};

export const CreateGoalModal = withCreateGoalFormProvider(
  ({ onClose = noop }: CreateGoalModalProps) => {
    const { handleSubmit, getValues, setError } = useFormContext<CreateGoalFormValues>();

    const dispatch = useDispatch();

    const {
      projectGoalsQuery: { projectGoals },
      missionControlUserPreferencesQuery: { refetch: refetchMissionControlUserPreferences },
    } = useMissionControlContext(); // TODO: this context should not be getting used here.

    const { mutate: updateGoalVisibility } = useUpdateGoalVisibilityMutation({
      onSuccess: () => {
        refetchMissionControlUserPreferences(); // NOTE: we will just use the response from `useUpdateGoalVisibilityMutation` in the future.
      },
    });

    const { mutate: createGoal, isLoading } = useCreateGoalMutation({
      onSuccess: (data) => {
        onClose();
        dispatch(
          addPositiveToast(<ToastMessage name={getValues('name')} suffix=" goal created." />),
        );

        /**
         * NOTE: This is a temporary solution to make the goal visible to the current user.
         * BE will be doing this automatically after the goal is created in the future.
         */
        updateGoalVisibility({
          goalId: data?.id as string,
          visibleToCurrentUser: true,
        });
      },
      onError: (error: any) => {
        const errorCode = error?.response?.data?.code;

        match(errorCode)
          .with('error.goals.goalEventNameInUse', () => {
            setError(EVENT_NAME_FIELD, { message: 'A goal with this event name already exists.' });
          })
          .with('error.goals.goalLimitError', () => {
            dispatch(
              addNegativeToast(
                <ToastMessage
                  name={getValues('name')}
                  suffix=" wasn’t created—your project has reached the 10-goal limit. Editing or removing goals isn’t supported yet."
                />,
              ),
            );
          })
          .otherwise(() => {
            dispatch(
              addNegativeToast(
                <ToastMessage
                  prefix="Unable to create "
                  name={getValues('name')}
                  suffix=" goal. Please try again."
                />,
              ),
            );
          });
      },
    });

    const onSubmit = (values: CreateGoalFormValues) => {
      const [eventType, eventName] = values?.eventName?.split(EVENT_TYPE_DELIMITER) ?? [];
      const isGoalNameInUse = projectGoals.some(
        (goal) => goal.name === values?.[GOAL_NAME_FIELD]?.trim(),
      );

      if (isGoalNameInUse) {
        setError(GOAL_NAME_FIELD, {
          message: 'This goal name already exists. Use a unique name to create.',
        });
      } else {
        createGoal({
          name: values?.name?.trim(),
          eventName,
          eventType,
          /**
           * These fields are not yet supported, so we're setting them to a default value for now.
           */
          attributionMode: GoalAttributionMode.NotSet,
          campaignLabelIds: [],
          messageTypeIds: [],
        });
      }
    };

    return (
      <Modal
        withPortal
        isModalOpen={true}
        onClose={onClose}
        id={CREATE_GOAL_MODAL_TEST_ID}
        data-test={CREATE_GOAL_MODAL_TEST_ID}
        size={MODAL_SIZES.MEDIUM}
      >
        <ModalHeader>New goal</ModalHeader>
        <ModalBody>
          <CreateGoalForm />
        </ModalBody>
        <ModalFooter>
          <PrimaryButton
            onClick={handleSubmit(onSubmit)}
            size={BUTTON_SIZES.LARGE}
            data-test={CREATE_GOAL_BUTTON_TEST_ID}
            isLoading={isLoading}
          >
            Create goal
          </PrimaryButton>
        </ModalFooter>
      </Modal>
    );
  },
);
